import type { FC } from 'react';
import { useMemo } from 'react';
import { useUserProfile } from 'src/contexts/UserProfileContext';
import { useEdrGlobalFiltersAccess } from 'src/mindtime/hooks/useEdrGlobalFiltersAccess';
import { useWazuhScope } from 'src/mindtime/hooks/useWazuhScope';
import { SecuritySearchableFilter } from './SecuritySearchableFilter';
import { useWazuhEndpointFilterOptions } from './useWazuhEndpointFilterOptions';
import { buildEndpointsApiScope } from './wazuhScopeParams';

const DEFAULT_PILL_LABEL = 'Endpoints';
const DEFAULT_ALL_LABEL = 'All Endpoints';
const DEFAULT_SEARCH_PLACEHOLDER = 'Search endpoints...';
const DEFAULT_EMPTY_MESSAGE = 'No endpoints found';
const DEFAULT_SELECTED_FALLBACK = 'Selected endpoint';

export type SecurityEndpointFilterProps = {
  /**
   * Controlled selected endpoint id(s) (`null` / `''` = all).
   * Omit both `value` and `onChange` to bind to global WazuhScope (`selectedEndpointId`).
   */
  value?: string | null;
  onChange?: (endpointId: string | null) => void;
  /** Page-local Organization filter (reseller); narrows endpoint options. */
  pageOrganizationId?: string;
  pillLabel?: string;
  allLabel?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  selectedFallbackLabel?: string;
  showFilterIcon?: boolean;
  fullWidth?: boolean;
  /** Fixed pill width (ignored when fullWidth). */
  minWidth?: number;
  multiple?: boolean;
};

/**
 * Searchable endpoints filter for all roles.
 * Hidden without `client_edr_global_filters_endpoint-read`.
 * Options from GET /api/v1/endpoints via useWazuhEndpointFilterOptions + buildEndpointsApiScope.
 * Default (uncontrolled): session-global `selectedEndpointId` via useWazuhScope.
 */
export const SecurityEndpointFilter: FC<SecurityEndpointFilterProps> = ({
  value: controlledValue,
  onChange: controlledOnChange,
  pageOrganizationId,
  pillLabel = DEFAULT_PILL_LABEL,
  allLabel = DEFAULT_ALL_LABEL,
  searchPlaceholder = DEFAULT_SEARCH_PLACEHOLDER,
  emptyMessage = DEFAULT_EMPTY_MESSAGE,
  selectedFallbackLabel = DEFAULT_SELECTED_FALLBACK,
  showFilterIcon = false,
  fullWidth = false,
  minWidth,
  multiple = false,
}) => {
  const { scope, isReseller, selectedEndpointId, setSelectedEndpointId } = useWazuhScope();
  const { canEndpoint } = useEdrGlobalFiltersAccess();
  const { userData } = useUserProfile();
  const profileOrgId = userData?.organization?.id;
  const isControlled = controlledOnChange !== undefined;
  const selectedId = isControlled ? (controlledValue ?? null) : selectedEndpointId;

  const listScope = useMemo(
    () =>
      buildEndpointsApiScope({
        scope,
        profileOrgId,
        isReseller,
        pageOrganizationId,
      }),
    [scope, profileOrgId, isReseller, pageOrganizationId]
  );

  const { options, loading, onSearch } = useWazuhEndpointFilterOptions(listScope);

  const handleChange = (endpointId: string | null) => {
    if (isControlled) {
      controlledOnChange?.(endpointId);
    } else {
      setSelectedEndpointId(endpointId);
    }
  };

  return (
    <SecuritySearchableFilter
      value={selectedId}
      onChange={handleChange}
      options={options}
      onSearch={onSearch}
      loading={loading}
      pillLabel={pillLabel}
      allLabel={allLabel}
      searchPlaceholder={searchPlaceholder}
      emptyMessage={emptyMessage}
      selectedFallbackLabel={selectedFallbackLabel}
      showFilterIcon={showFilterIcon}
      fullWidth={fullWidth}
      minWidth={minWidth}
      multiple={multiple}
      visible={canEndpoint}
    />
  );
};
